/**
 * City Agency Pages — Wave 9
 * Minsk, Astana, Bishkek, Chisinau, Sarajevo, Tirana, Nicosia, Valletta, Luxembourg, Reykjavik
 */
import type { CityAgencyData } from './city-agency-pages';

const sharedServices = [
  { name: 'Custom SaaS Development', description: 'End-to-end SaaS product development: architecture, development, deployment, and scaling.' },
  { name: 'Web Application Development', description: 'Enterprise web applications, portals, dashboards, and internal tools with modern stack.' },
  { name: 'Mobile App Development', description: 'Native iOS/Android and cross-platform apps with React Native and Flutter.' },
  { name: 'AI & Machine Learning', description: 'AI-powered solutions: NLP, computer vision, predictive analytics, and LLM integration.' },
  { name: 'Cloud & DevOps', description: 'AWS, Azure, GCP infrastructure. CI/CD pipelines, containerization, and managed services.' },
  { name: 'Staff Augmentation', description: 'Dedicated developers, QA engineers, DevOps specialists integrated into your team.' },
  { name: 'UI/UX Design', description: 'User research, wireframing, prototyping, and design systems with accessibility compliance.' },
  { name: 'Cybersecurity', description: 'Penetration testing, security audits, SOC operations, and compliance consulting.' },
];

const sharedIndustries = [
  { name: 'FinTech & Banking', description: 'Payment platforms, trading systems, compliance tools with PCI-DSS and SOC 2.' },
  { name: 'E-commerce & Retail', description: 'Online stores, marketplace platforms, inventory management solutions.' },
  { name: 'Healthcare & MedTech', description: 'Patient portals, telemedicine, EHR integrations, HIPAA-compliant applications.' },
  { name: 'SaaS & Technology', description: 'Product development and modernization for SaaS companies at any stage.' },
  { name: 'Real Estate & PropTech', description: 'Property management platforms, virtual tours, and real estate marketplaces.' },
  { name: 'Logistics & Supply Chain', description: 'Fleet management, route optimization, warehouse and supply chain platforms.' },
];

export const CITY_AGENCY_PAGES_WAVE9: Record<string, CityAgencyData> = {

  minsk: {
    slug: 'software-development-agency-minsk',
    cityName: 'Minsk',
    countryName: 'Belarus',
    flag: '🇧🇾',
    timezone: 'MSK (GMT+3)',
    currency: 'BYN',
    title: {
      en: 'Software Development Agency Minsk | Call IT Dev',
      fr: 'Agence Développement Informatique Minsk | Call IT Dev',
      ru: 'Агентство Разработки ПО Минск | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Minsk, Belarus. Custom SaaS, web, mobile, AI. From $18/hr. 65% savings vs Western rates.',
      fr: 'Agence développement informatique pour Minsk. SaaS, web, mobile, IA. Dès 18 $/h. 65% d\'économies.',
      ru: 'Агентство разработки ПО для Минска. SaaS, web, mobile, AI. От 18 $/ч. Экономия 65%.',
    },
    keywords: ['software development agency Minsk', 'IT outsourcing Belarus', 'nearshore development Minsk', 'web development Belarus'],
    heroTitle: {
      en: 'Software Development Agency for Minsk',
      fr: 'Agence de Développement Informatique pour Minsk',
      ru: 'Агентство Разработки ПО для Минска',
    },
    heroSubtitle: {
      en: 'Russian & English speaking engineers, 2h time difference from CET, 65% savings. Enterprise-grade nearshore development.',
      fr: 'Ingénieurs russophones et anglophones, 2h de décalage CET, 65% d\'économies.',
      ru: 'Русскоговорящие инженеры, 2 часа от CET, экономия 65%.',
    },
    whyTitle: { en: 'Why Minsk Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises de Minsk Choisissent Call IT Dev', ru: 'Почему Компании Минска Выбирают Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'Russian & English', description: 'Bilingual teams for CIS and international markets.' },
      { icon: '💰', title: '65% Savings', description: 'Senior devs at $18-30/hr vs $50-80/hr in Western Europe.' },
      { icon: '🎓', title: 'Engineering Talent', description: '200+ engineers across modern tech stacks.' },
      { icon: '⏰', title: '2h Time Difference', description: 'Strong overlap with European working hours.' },
      { icon: '🛡️', title: 'Security First', description: 'ISO 27001, GDPR compliance.' },
      { icon: '🚀', title: 'Fast Setup', description: 'Team ready in 2 weeks.' },
    ],
    services: sharedServices, industries: sharedIndustries,
    faqs: [
      { question: 'Russian-speaking teams?', answer: 'Yes. Native Russian speakers for CIS markets.' },
      { question: 'Technologies?', answer: 'React, Node.js, Python, Java, AWS, Azure, GCP.' },
      { question: 'Data security?', answer: 'ISO 27001, GDPR compliant.' },
      { question: 'How to start?', answer: 'Free consultation, team in 2 weeks.' },
      { question: 'Minimum team?', answer: '2 developers, scale to 50+.' },
      { question: 'Contracts?', answer: 'Monthly flexibility, no lock-in.' },
    ],
    localRate: '$40-65', ourRate: '$18-30', savings: '65%+',
  },

  astana: {
    slug: 'software-development-agency-astana',
    cityName: 'Astana',
    countryName: 'Kazakhstan',
    flag: '🇰🇿',
    timezone: 'ALMT (GMT+6)',
    currency: 'KZT',
    title: {
      en: 'Software Development Agency Astana | Call IT Dev',
      fr: 'Agence Développement Informatique Astana | Call IT Dev',
      ru: 'Агентство Разработки ПО Астана | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Astana, Kazakhstan. Custom SaaS, web, mobile, AI. From $18/hr. 70% savings.',
      fr: 'Agence développement informatique pour Astana. SaaS, web, mobile, IA. Dès 18 $/h.',
      ru: 'Агентство разработки ПО для Астаны. SaaS, web, mobile, AI. От 18 $/ч. Экономия 70%.',
    },
    keywords: ['software development agency Astana', 'IT outsourcing Kazakhstan', 'nearshore development Astana', 'web development Kazakhstan'],
    heroTitle: { en: 'Software Development Agency for Astana', fr: 'Agence de Développement Informatique pour Astana', ru: 'Агентство Разработки ПО для Астаны' },
    heroSubtitle: {
      en: 'Russian & English engineers, 70% savings. Kazakhstan\'s tech hub meets nearshore excellence.',
      fr: 'Ingénieurs russophones et anglophones, 70% d\'économies. Hub tech kazakh.',
      ru: 'Русскоговорящие инженеры, экономия 70%. IT-хаб Казахстана.',
    },
    whyTitle: { en: 'Why Astana Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises d\'Astana Choisissent Call IT Dev', ru: 'Почему Компании Астаны Выбирают Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'Russian & English', description: 'Bilingual teams for CIS and global markets.' },
      { icon: '💰', title: '70% Savings', description: 'Enterprise-grade development at competitive rates.' },
      { icon: '🎓', title: 'Expert Engineers', description: '200+ specialists across modern stacks.' },
      { icon: '🌍', title: 'CIS Expertise', description: 'Deep Central Asian business understanding.' },
      { icon: '🛡️', title: 'Enterprise Security', description: 'ISO 27001, GDPR compliance.' },
      { icon: '🚀', title: 'Quick Start', description: 'Team operational in 2 weeks.' },
    ],
    services: sharedServices, industries: sharedIndustries,
    faqs: [
      { question: 'Russian-speaking teams?', answer: 'Yes. Native Russian and English speakers.' },
      { question: 'Technologies?', answer: 'React, Node.js, Python, Java, AWS, Azure, GCP.' },
      { question: 'Security?', answer: 'ISO 27001, GDPR compliant.' },
      { question: 'How to start?', answer: 'Free consultation, team in 2 weeks.' },
      { question: 'Minimum team?', answer: '2 developers, scale to 50+.' },
      { question: 'IP ownership?', answer: 'All IP belongs to you from day one.' },
    ],
    localRate: '$30-50', ourRate: '$18-30', savings: '70%+',
  },

  bishkek: {
    slug: 'software-development-agency-bishkek',
    cityName: 'Bishkek',
    countryName: 'Kyrgyzstan',
    flag: '🇰🇬',
    timezone: 'KGT (GMT+6)',
    currency: 'KGS',
    title: {
      en: 'Software Development Agency Bishkek | Call IT Dev',
      fr: 'Agence Développement Informatique Bichkek | Call IT Dev',
      ru: 'Агентство Разработки ПО Бишкек | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Bishkek, Kyrgyzstan. Custom SaaS, web, mobile, AI. From $15/hr. 75% savings.',
      fr: 'Agence développement informatique pour Bichkek. SaaS, web, mobile, IA. Dès 15 $/h.',
      ru: 'Агентство разработки ПО для Бишкека. SaaS, web, mobile, AI. От 15 $/ч.',
    },
    keywords: ['software development agency Bishkek', 'IT outsourcing Kyrgyzstan', 'nearshore development Central Asia'],
    heroTitle: { en: 'Software Development Agency for Bishkek', fr: 'Agence de Développement Informatique pour Bichkek', ru: 'Агентство Разработки ПО для Бишкека' },
    heroSubtitle: {
      en: 'Russian & English engineers, 75% savings. Emerging Central Asian talent at nearshore prices.',
      fr: 'Ingénieurs russophones, 75% d\'économies. Talents émergents d\'Asie Centrale.',
      ru: 'Русскоговорящие инженеры, экономия 75%. Таланты Центральной Азии.',
    },
    whyTitle: { en: 'Why Bishkek Companies Choose Call IT Dev', fr: 'Pourquoi Choisir Call IT Dev depuis Bichkek', ru: 'Почему Компании Бишкека Выбирают Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'Russian & English', description: 'Bilingual teams for CIS markets.' },
      { icon: '💰', title: '75% Savings', description: 'Highly competitive nearshore rates.' },
      { icon: '🎓', title: 'Growing Talent', description: 'Access to emerging engineering talent pool.' },
      { icon: '🛡️', title: 'Security', description: 'ISO 27001, GDPR compliance.' },
      { icon: '🌍', title: 'CIS Markets', description: 'Expertise in CIS business landscape.' },
      { icon: '🚀', title: 'Fast Delivery', description: 'Team operational in 2 weeks.' },
    ],
    services: sharedServices, industries: sharedIndustries,
    faqs: [
      { question: 'Languages?', answer: 'Russian and English-speaking teams.' },
      { question: 'Technologies?', answer: 'React, Node.js, Python, Java, cloud-native.' },
      { question: 'Security?', answer: 'ISO 27001, GDPR compliant.' },
      { question: 'How to start?', answer: 'Free consultation, team in 2 weeks.' },
      { question: 'Scaling?', answer: '2 to 50+ developers.' },
      { question: 'Contracts?', answer: 'Monthly flexibility.' },
    ],
    localRate: '$25-40', ourRate: '$15-25', savings: '75%+',
  },

  chisinau: {
    slug: 'software-development-agency-chisinau',
    cityName: 'Chișinău',
    countryName: 'Moldova',
    flag: '🇲🇩',
    timezone: 'EET (GMT+2)',
    currency: 'MDL',
    title: {
      en: 'Software Development Agency Chișinău | Call IT Dev',
      fr: 'Agence Développement Informatique Chișinău | Call IT Dev',
      ru: 'Агентство Разработки ПО Кишинёв | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Chișinău, Moldova. Custom SaaS, web, mobile, AI. From $18/hr. 65% savings. Romanian/Russian/English.',
      fr: 'Agence développement informatique pour Chișinău. SaaS, web, mobile, IA. Dès 18 $/h. Roumain/Russe/Anglais.',
      ru: 'Агентство разработки ПО для Кишинёва. SaaS, web, mobile, AI. От 18 $/ч. Румынский/Русский/Английский.',
    },
    keywords: ['software development agency Chisinau', 'IT outsourcing Moldova', 'nearshore development Chisinau'],
    heroTitle: { en: 'Software Development Agency for Chișinău', fr: 'Agence de Développement Informatique pour Chișinău', ru: 'Агентство Разработки ПО для Кишинёва' },
    heroSubtitle: {
      en: 'Romanian, Russian & English engineers, 1h time difference, 65% savings. Moldova\'s EU-aspirant tech sector.',
      fr: 'Ingénieurs roumains, russes et anglophones, 1h de décalage, 65% d\'économies.',
      ru: 'Румыно-, русско- и англоговорящие инженеры, 1 час разницы, экономия 65%.',
    },
    whyTitle: { en: 'Why Chișinău Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises de Chișinău Choisissent Call IT Dev', ru: 'Почему Компании Кишинёва Выбирают Call IT Dev' },
    whyReasons: [
      { icon: '🗣️', title: 'Trilingual Teams', description: 'Romanian, Russian, and English fluency.' },
      { icon: '⏰', title: '1h Time Difference', description: 'Near-identical working hours.' },
      { icon: '💰', title: '65% Savings', description: 'Senior devs at $18-30/hr vs $45-65/hr.' },
      { icon: '🇪🇺', title: 'EU-Aspirant', description: 'Aligned with EU standards and regulations.' },
      { icon: '🛡️', title: 'GDPR Ready', description: 'ISO 27001, GDPR-aligned processes.' },
      { icon: '🚀', title: 'Quick Start', description: 'Team ready in 2 weeks.' },
    ],
    services: sharedServices, industries: sharedIndustries,
    faqs: [
      { question: 'Languages?', answer: 'Romanian, Russian, English, French.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure.' },
      { question: 'GDPR?', answer: 'GDPR-aligned, ISO 27001.' },
      { question: 'How to start?', answer: 'CVs in 5 days, team in 2 weeks.' },
      { question: 'Scaling?', answer: '2 to 50+ developers.' },
      { question: 'IP?', answer: 'All IP belongs to you.' },
    ],
    localRate: '$35-55', ourRate: '$18-30', savings: '65%+',
  },

  sarajevo: {
    slug: 'software-development-agency-sarajevo',
    cityName: 'Sarajevo',
    countryName: 'Bosnia & Herzegovina',
    flag: '🇧🇦',
    timezone: 'CET (GMT+1)',
    currency: 'BAM',
    title: {
      en: 'Software Development Agency Sarajevo | Call IT Dev',
      fr: 'Agence Développement Informatique Sarajevo | Call IT Dev',
      de: 'Softwareentwicklung Agentur Sarajevo | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Sarajevo. Custom SaaS, web, mobile, AI. CET timezone, from €18/hr. 60% savings.',
      fr: 'Agence développement informatique pour Sarajevo. SaaS, web, mobile, IA. Fuseau CET, dès 18 €/h.',
      de: 'Softwareentwicklungsagentur für Sarajevo. SaaS, Web, Mobile, KI. CET, ab 18 €/h.',
    },
    keywords: ['software development agency Sarajevo', 'IT outsourcing Bosnia', 'nearshore development Sarajevo'],
    heroTitle: { en: 'Software Development Agency for Sarajevo', fr: 'Agence de Développement Informatique pour Sarajevo', de: 'Softwareentwicklung Agentur für Sarajevo' },
    heroSubtitle: {
      en: 'Same CET timezone, 60% savings. Balkan engineering talent at nearshore prices.',
      fr: 'Même fuseau CET, 60% d\'économies. Talents des Balkans à prix nearshore.',
      de: 'Gleiche CET-Zeitzone, 60% Ersparnis. Balkan-Talente zu Nearshore-Preisen.',
    },
    whyTitle: { en: 'Why Sarajevo Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises de Sarajevo Choisissent Call IT Dev', de: 'Warum Sarajevoer Unternehmen Call IT Dev Wählen' },
    whyReasons: [
      { icon: '⏰', title: 'Same CET', description: 'Identical working hours for real-time collaboration.' },
      { icon: '💰', title: '60% Savings', description: 'Senior devs at €18-30/hr vs €40-60/hr.' },
      { icon: '🎓', title: 'Engineering Talent', description: '200+ engineers across modern stacks.' },
      { icon: '🌍', title: 'Multilingual', description: 'English, German, French speaking teams.' },
      { icon: '🛡️', title: 'GDPR Aligned', description: 'ISO 27001, EU standards.' },
      { icon: '🚀', title: '2-Week Setup', description: 'Fast onboarding.' },
    ],
    services: sharedServices, industries: sharedIndustries,
    faqs: [
      { question: 'Timezone?', answer: 'Same CET as Central Europe.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, cloud-native.' },
      { question: 'Security?', answer: 'ISO 27001, GDPR-aligned.' },
      { question: 'How to start?', answer: 'CVs in 5 days, team in 2 weeks.' },
      { question: 'Contracts?', answer: 'Monthly flexibility.' },
      { question: 'Scaling?', answer: '2 to 50+ developers.' },
    ],
    localRate: '€35-55', ourRate: '€18-30', savings: '60%+',
  },

  tirana: {
    slug: 'software-development-agency-tirana',
    cityName: 'Tirana',
    countryName: 'Albania',
    flag: '🇦🇱',
    timezone: 'CET (GMT+1)',
    currency: 'ALL/EUR',
    title: {
      en: 'Software Development Agency Tirana | Call IT Dev',
      fr: 'Agence Développement Informatique Tirana | Call IT Dev',
      it: 'Agenzia Sviluppo Software Tirana | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Tirana, Albania. Custom SaaS, web, mobile, AI. CET timezone, from €18/hr. 60% savings.',
      fr: 'Agence développement informatique pour Tirana. SaaS, web, mobile, IA. Fuseau CET, dès 18 €/h.',
      it: 'Agenzia di sviluppo software per Tirana. SaaS, web, mobile, IA. Fuso CET, da 18 €/h.',
    },
    keywords: ['software development agency Tirana', 'IT outsourcing Albania', 'nearshore development Tirana'],
    heroTitle: { en: 'Software Development Agency for Tirana', fr: 'Agence de Développement Informatique pour Tirana', it: 'Agenzia di Sviluppo Software per Tirana' },
    heroSubtitle: {
      en: 'Same CET timezone, 60% savings. Albania\'s growing tech scene meets nearshore excellence.',
      fr: 'Même fuseau CET, 60% d\'économies. La scène tech albanaise.',
      it: 'Stesso fuso CET, 60% di risparmio. La scena tech albanese.',
    },
    whyTitle: { en: 'Why Tirana Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises de Tirana Choisissent Call IT Dev', it: 'Perché le Aziende di Tirana Scelgono Call IT Dev' },
    whyReasons: [
      { icon: '⏰', title: 'Same CET', description: 'Identical working hours.' },
      { icon: '💰', title: '60% Savings', description: 'Senior devs at €18-30/hr vs €35-55/hr.' },
      { icon: '🎓', title: 'Growing Talent', description: 'Access Albania\'s emerging tech talent.' },
      { icon: '🌍', title: 'Italian & English', description: 'Italian and English speaking teams.' },
      { icon: '🛡️', title: 'EU Candidate', description: 'GDPR-aligned, EU aspirant standards.' },
      { icon: '🚀', title: 'Fast Delivery', description: 'Team in 2 weeks.' },
    ],
    services: sharedServices, industries: sharedIndustries,
    faqs: [
      { question: 'Timezone?', answer: 'Same CET as Italy, France, Germany.' },
      { question: 'Technologies?', answer: 'React, Node.js, Python, Java, AWS, Azure, GCP.' },
      { question: 'Languages?', answer: 'English, Italian, French speaking engineers.' },
      { question: 'How to start?', answer: 'Free consultation, team in 2 weeks.' },
      { question: 'Contracts?', answer: 'Monthly, no lock-in.' },
      { question: 'Scaling?', answer: '2 to 50+.' },
    ],
    localRate: '€30-50', ourRate: '€18-30', savings: '60%+',
  },

  nicosia: {
    slug: 'software-development-agency-nicosia',
    cityName: 'Nicosia',
    countryName: 'Cyprus',
    flag: '🇨🇾',
    timezone: 'EET (GMT+2)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Nicosia | Call IT Dev',
      fr: 'Agence Développement Informatique Nicosie | Call IT Dev',
      de: 'Softwareentwicklung Agentur Nikosia | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Nicosia, Cyprus. Custom SaaS, web, mobile, AI. From €22/hr. 55% savings vs local rates.',
      fr: 'Agence développement informatique pour Nicosie. SaaS, web, mobile, IA. Dès 22 €/h.',
      de: 'Softwareentwicklungsagentur für Nikosia. SaaS, Web, Mobile, KI. Ab 22 €/h.',
    },
    keywords: ['software development agency Nicosia', 'IT outsourcing Cyprus', 'nearshore development Cyprus'],
    heroTitle: { en: 'Software Development Agency for Nicosia', fr: 'Agence de Développement Informatique pour Nicosie', de: 'Softwareentwicklung Agentur für Nikosia' },
    heroSubtitle: {
      en: '1-hour time difference, 55% savings. EU-member Cyprus meets nearshore efficiency.',
      fr: '1h de décalage, 55% d\'économies. Chypre, membre UE, rencontre l\'efficacité nearshore.',
      de: '1h Zeitunterschied, 55% Ersparnis. EU-Mitglied Zypern trifft Nearshore-Effizienz.',
    },
    whyTitle: { en: 'Why Nicosia Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises de Nicosie Choisissent Call IT Dev', de: 'Warum Unternehmen in Nikosia Call IT Dev Wählen' },
    whyReasons: [
      { icon: '⏰', title: '1h Difference', description: 'Near-identical working hours.' },
      { icon: '💰', title: '55% Savings', description: 'Senior devs at €22-35/hr vs €50-80/hr.' },
      { icon: '🇪🇺', title: 'EU Standards', description: 'ISO 27001, GDPR, EU compliance.' },
      { icon: '🎓', title: '200+ Engineers', description: 'Full-stack, AI, DevOps.' },
      { icon: '🌍', title: 'English & Greek', description: 'Multilingual communication.' },
      { icon: '🚀', title: 'Quick Start', description: 'Team in 2 weeks.' },
    ],
    services: sharedServices, industries: sharedIndustries,
    faqs: [
      { question: 'EU compliance?', answer: 'Full GDPR, ISO 27001, EU data protection.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, cloud-native.' },
      { question: 'How to start?', answer: 'CVs in 5 days, team in 2 weeks.' },
      { question: 'Contracts?', answer: 'Monthly flexibility.' },
      { question: 'IP?', answer: 'All IP yours from day one.' },
      { question: 'Scaling?', answer: '2 to 50+ developers.' },
    ],
    localRate: '€50-80', ourRate: '€22-35', savings: '55%+',
  },

  valletta: {
    slug: 'software-development-agency-valletta',
    cityName: 'Valletta',
    countryName: 'Malta',
    flag: '🇲🇹',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Valletta | Call IT Dev',
      fr: 'Agence Développement Informatique La Valette | Call IT Dev',
      it: 'Agenzia Sviluppo Software La Valletta | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Valletta, Malta. Custom SaaS, web, mobile, AI. CET timezone, from €22/hr. 55% savings.',
      fr: 'Agence développement informatique pour La Valette. SaaS, web, mobile, IA. CET, dès 22 €/h.',
      it: 'Agenzia sviluppo software per La Valletta. SaaS, web, mobile, IA. CET, da 22 €/h.',
    },
    keywords: ['software development agency Valletta', 'IT outsourcing Malta', 'nearshore development Malta', 'iGaming development Malta'],
    heroTitle: { en: 'Software Development Agency for Valletta', fr: 'Agence de Développement Informatique pour La Valette', it: 'Agenzia di Sviluppo Software per La Valletta' },
    heroSubtitle: {
      en: 'Same CET timezone, 55% savings. Malta\'s iGaming & FinTech hub meets nearshore development.',
      fr: 'Même fuseau CET, 55% d\'économies. Le hub iGaming et FinTech maltais.',
      it: 'Stesso fuso CET, 55% risparmio. L\'hub iGaming e FinTech maltese.',
    },
    whyTitle: { en: 'Why Valletta Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises de La Valette Choisissent Call IT Dev', it: 'Perché le Aziende de La Valletta Scelgono Call IT Dev' },
    whyReasons: [
      { icon: '⏰', title: 'Same CET', description: 'Identical working hours.' },
      { icon: '💰', title: '55% Savings', description: 'Senior devs at €22-35/hr vs €55-90/hr.' },
      { icon: '🎰', title: 'iGaming Expertise', description: 'Deep experience with Malta\'s iGaming sector.' },
      { icon: '🇪🇺', title: 'EU Member', description: 'Full GDPR and EU compliance.' },
      { icon: '🎓', title: 'Engineering Talent', description: '200+ specialists.' },
      { icon: '🚀', title: 'Fast Setup', description: 'Team in 2 weeks.' },
    ],
    services: sharedServices, industries: sharedIndustries,
    faqs: [
      { question: 'iGaming experience?', answer: 'Yes. Deep expertise in iGaming platforms, back-office, and regulatory tech.' },
      { question: 'Technologies?', answer: 'React, Node.js, Python, Java, AWS, Azure, blockchain.' },
      { question: 'EU compliance?', answer: 'Full GDPR, ISO 27001.' },
      { question: 'How to start?', answer: 'Free consultation, team in 2 weeks.' },
      { question: 'Contracts?', answer: 'Monthly, no lock-in.' },
      { question: 'Scaling?', answer: '2 to 50+.' },
    ],
    localRate: '€55-90', ourRate: '€22-35', savings: '55%+',
  },

  luxembourg: {
    slug: 'software-development-agency-luxembourg',
    cityName: 'Luxembourg',
    countryName: 'Luxembourg',
    flag: '🇱🇺',
    timezone: 'CET (GMT+1)',
    currency: 'EUR',
    title: {
      en: 'Software Development Agency Luxembourg | Call IT Dev',
      fr: 'Agence Développement Informatique Luxembourg | Call IT Dev',
      de: 'Softwareentwicklung Agentur Luxemburg | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Luxembourg. Custom SaaS, web, mobile, AI. CET timezone, from €25/hr. 60% savings vs local rates.',
      fr: 'Agence développement informatique pour Luxembourg. SaaS, web, mobile, IA. CET, dès 25 €/h. 60% d\'économies.',
      de: 'Softwareentwicklungsagentur für Luxemburg. SaaS, Web, Mobile, KI. CET, ab 25 €/h. 60% Ersparnis.',
    },
    keywords: ['software development agency Luxembourg', 'IT outsourcing Luxembourg', 'nearshore development Luxembourg', 'FinTech development Luxembourg'],
    heroTitle: { en: 'Software Development Agency for Luxembourg', fr: 'Agence de Développement Informatique pour Luxembourg', de: 'Softwareentwicklung Agentur für Luxemburg' },
    heroSubtitle: {
      en: 'Same CET timezone, French/English/German engineers, 60% savings. Luxembourg\'s financial hub meets nearshore talent.',
      fr: 'Même fuseau CET, ingénieurs FR/EN/DE, 60% d\'économies. Le hub financier luxembourgeois.',
      de: 'Gleiche CET-Zeitzone, FR/EN/DE-Ingenieure, 60% Ersparnis. Luxemburgs Finanzhub.',
    },
    whyTitle: { en: 'Why Luxembourg Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises Luxembourgeoises Choisissent Call IT Dev', de: 'Warum Luxemburger Unternehmen Call IT Dev Wählen' },
    whyReasons: [
      { icon: '⏰', title: 'Same CET', description: 'Identical timezone for real-time work.' },
      { icon: '🗣️', title: 'FR/EN/DE', description: 'Trilingual teams matching Luxembourg\'s needs.' },
      { icon: '💰', title: '60% Savings', description: 'Senior devs at €25-40/hr vs €90-150/hr.' },
      { icon: '🏦', title: 'FinTech Focus', description: 'Deep banking and fund management expertise.' },
      { icon: '🛡️', title: 'EU Compliant', description: 'ISO 27001, GDPR, CSSF-ready.' },
      { icon: '🚀', title: 'Quick Start', description: 'Team in 2 weeks.' },
    ],
    services: sharedServices, industries: sharedIndustries,
    faqs: [
      { question: 'Languages?', answer: 'French, English, German — matching Luxembourg\'s trilingual needs.' },
      { question: 'FinTech experience?', answer: 'Yes. Banking, fund administration, regulatory tech, PSD2, CSSF compliance.' },
      { question: 'GDPR?', answer: 'Full GDPR, ISO 27001, EU compliance.' },
      { question: 'How to start?', answer: 'Free consultation, team in 2 weeks.' },
      { question: 'Cost comparison?', answer: '€25-40/hr vs €90-150/hr locally. 60%+ savings.' },
      { question: 'IP?', answer: 'All IP belongs to you.' },
    ],
    localRate: '€90-150', ourRate: '€25-40', savings: '60%+',
  },

  reykjavik: {
    slug: 'software-development-agency-reykjavik',
    cityName: 'Reykjavik',
    countryName: 'Iceland',
    flag: '🇮🇸',
    timezone: 'GMT (GMT+0)',
    currency: 'ISK',
    title: {
      en: 'Software Development Agency Reykjavik | Call IT Dev',
      fr: 'Agence Développement Informatique Reykjavik | Call IT Dev',
      sv: 'Mjukvaruutveckling Byrå Reykjavik | Call IT Dev',
    },
    description: {
      en: 'Software development agency serving Reykjavik, Iceland. Custom SaaS, web, mobile, AI. From €25/hr. 60% savings vs Icelandic rates.',
      fr: 'Agence développement informatique pour Reykjavik. SaaS, web, mobile, IA. Dès 25 €/h. 60% d\'économies.',
      sv: 'Mjukvaruutvecklingsbyrå för Reykjavik. SaaS, webb, mobil, AI. Från 25 €/h. 60% besparing.',
    },
    keywords: ['software development agency Reykjavik', 'IT outsourcing Iceland', 'nearshore development Iceland'],
    heroTitle: { en: 'Software Development Agency for Reykjavik', fr: 'Agence de Développement Informatique pour Reykjavik', sv: 'Mjukvaruutveckling Byrå för Reykjavik' },
    heroSubtitle: {
      en: '1-hour time difference, 60% savings vs Iceland rates. Nordic quality at nearshore prices.',
      fr: '1h de décalage, 60% d\'économies vs tarifs islandais. Qualité nordique à prix nearshore.',
      sv: '1h tidsskillnad, 60% besparing vs isländska priser. Nordisk kvalitet till nearshore-priser.',
    },
    whyTitle: { en: 'Why Reykjavik Companies Choose Call IT Dev', fr: 'Pourquoi les Entreprises de Reykjavik Choisissent Call IT Dev', sv: 'Varför Reykjavik-Företag Väljer Call IT Dev' },
    whyReasons: [
      { icon: '⏰', title: '1h Difference', description: 'Close timezone alignment with Iceland.' },
      { icon: '💰', title: '60% Savings', description: 'Senior devs at €25-40/hr vs €80-130/hr.' },
      { icon: '🌍', title: 'English Fluent', description: 'C1/C2 English-speaking engineers.' },
      { icon: '🎓', title: '200+ Engineers', description: 'Full-stack, AI, DevOps specialists.' },
      { icon: '🛡️', title: 'EEA Compliant', description: 'ISO 27001, GDPR, EEA standards.' },
      { icon: '🚀', title: 'Quick Start', description: 'Team in 2 weeks.' },
    ],
    services: sharedServices, industries: sharedIndustries,
    faqs: [
      { question: 'Timezone?', answer: '1 hour difference. Full day overlap.' },
      { question: 'Technologies?', answer: 'React, TypeScript, Node.js, Python, Java, AWS, Azure, GCP.' },
      { question: 'GDPR?', answer: 'Full GDPR/EEA compliance, ISO 27001.' },
      { question: 'How to start?', answer: 'Free consultation, team in 2 weeks.' },
      { question: 'Cost?', answer: '€25-40/hr vs €80-130/hr in Iceland. 60%+ savings.' },
      { question: 'Scaling?', answer: '2 to 50+ developers.' },
    ],
    localRate: '€80-130', ourRate: '€25-40', savings: '60%+',
  },
};
